import React from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { apiFetch } from '@/lib/api';
import { appConfig } from '@/config/app';
import { t } from '@/lib/i18n';

export function ForgotPasswordPage() {
  const [email, setEmail] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      const res = await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
        skipRefresh: true,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error?.message || t('Something went wrong'));
      }
      setSent(true);
    } catch (err: any) {
      setError(err.message || t('Something went wrong. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/auth" className="inline-block hover:opacity-80 transition-opacity">
            <img
              src="/images/logo.png"
              alt={appConfig.name}
              className="h-8 mx-auto mb-4 object-contain"
            />
            <p className="text-sm text-muted-foreground mt-1">Company Automation Platform</p>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-8">
            {sent ? (
              <div className="text-center">
                <div className="w-14 h-14 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Check your email</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  If an account with that email exists, we've sent a password reset link. Check your inbox and spam folder.
                </p>
                <Link
                  to="/auth"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Reset your password</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-5">
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />

                  {error && (
                    <Alert variant="error">{error}</Alert>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full"
                    loading={isLoading}
                  >
                    Send Reset Link
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/auth"
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Back to sign in
                    </Link>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </div>
      </div>
    </div>
  );
}
